import { createSelector } from 'reselect'

import { getParsedDateUntilToday, getRangeFromDateFilter } from 'utils/dateUtils'
import {
  cutArrayToTopAndBottom,
  formatSecondsToDuration,
  normalizePercent,
  normalizeReportData,
  sum,
} from 'utils/helpers'

const selectStateData = (state) => state.dashboard.awareness.self.data
const selectLoader = (state) => state.dashboard.awareness.self.loader
const selectError = (state) => state.dashboard.awareness.self.error
const selectTimeFilter = (state) => state.dashboard.awareness.self.dateFilter
const selectCacheKey = (state) => state.dashboard.awareness.self.cacheKey
const selectFeDateRange = (state) => state.dashboard.awareness.self.dateRange
const selectSelectedCampaignIds = (state) => state.dashboard.awareness.self.data.selectedCampaignIds

const selectDateRange = createSelector(
  selectFeDateRange,
  selectTimeFilter,
  (feDateRange, dateFilter) => feDateRange || getRangeFromDateFilter(dateFilter)
)

const selectParsedDate = createSelector(selectTimeFilter, (currentDateFilter) =>
  getParsedDateUntilToday(currentDateFilter)
)

const selectData = createSelector(selectStateData, (stateData) => {
  let rd = stateData.reportData
  let bestReportTimeText = 'N/A'
  let avgReportTimeText = 'N/A'
  const selectedCampaignList =
    stateData.campaigns
      .map((elem) => ({ value: elem.id, label: elem.name }))
      .filter((campaign) => stateData.selectedCampaignIds?.includes(campaign.value)) || []
  if (Object.keys(rd).length) {
    const nrd = normalizeReportData(stateData.reportData)
    if (nrd.firstReport) {
      const filteredReportTimes = nrd.firstReport.filter((t) => !!t)
      if (filteredReportTimes.length) {
        const bestReportTime = Math.min(...filteredReportTimes)
        const avgReportTime = Math.floor(
          filteredReportTimes.reduce(sum, 0) / filteredReportTimes.length
        )
        bestReportTimeText = formatSecondsToDuration(bestReportTime)
        avgReportTimeText = formatSecondsToDuration(avgReportTime)
      }
    }
    const deptsClickRates = Object.keys(nrd.deptsClickRates)
      .filter((k) => nrd.deptsClickRates[k])
      .sort((k1, k2) => nrd.deptsClickRates[k1] - nrd.deptsClickRates[k2])
      .map((k) => ({
        name: k,
        value: normalizePercent(nrd.deptsClickRates[k]),
      }))

    const scenarioClickFrequency = Object.keys(nrd.scenarioClickFrequency)
      .filter((k) => nrd.scenarioClickFrequency[k])
      .sort((k1, k2) => nrd.scenarioClickFrequency[k1] - nrd.scenarioClickFrequency[k2])
      .map((k) => ({
        name: k,
        value: normalizePercent(nrd.scenarioClickFrequency[k]),
      }))
      .reverse()
    rd = {
      ...nrd,
      deptsClickRates: cutArrayToTopAndBottom(deptsClickRates),
      scenarioClickFrequency: cutArrayToTopAndBottom(scenarioClickFrequency),
      clicksPerLocationOverall: Object.keys(nrd.clicksPerLocationOverall)
        .filter((k) => nrd.clicksPerLocationOverall[k])
        .sort((k1, k2) => nrd.clicksPerLocationOverall[k1] - nrd.clicksPerLocationOverall[k2])
        .map((k) => ({
          name: k,
          value: nrd.clicksPerLocationOverall[k],
        }))
        .reverse(),
    }
  }

  return {
    ...stateData,
    campaigns: stateData.campaigns.map((elem) => ({
      value: elem.id,
      label: elem.name,
    })),
    selectedCampaignList,
    reportData: rd,
    bestReportTimeText,
    avgReportTimeText,
  }
})

export {
  selectData,
  selectLoader,
  selectError,
  selectCacheKey,
  selectTimeFilter,
  selectParsedDate,
  selectDateRange,
  selectSelectedCampaignIds,
}
